import {
    h, Component, ComponentType, createRef, VNode, cloneElement, RefCallback
} from "preact";

import { arrayEqual } from "../../../foundation/utils/array";

import MenuContext, { HighlightableMenuItem } from "./MenuContext";

interface MenuChildrenInjectedProps {
    indicesPath?: string[];
    highlighted?: boolean;
    open?: boolean;
    autoScrollWithArrowKeys?: boolean;
    handleMenuChildClick?: (event: Event, value?: string) => void;
    handleMenuChildMouseEnter?: (indicesPath: string[]) => void;
    handleMenuChildMouseMove?: (indicesPath: string[]) => void;
    handleMenuChildMouseLeave?: () => void;
}

interface MenuItemRequiredProps extends MenuChildrenInjectedProps {
    content?: string;
    value?: string;
    disabled?: boolean;
}

interface MenuItemInjectedProps {
    menuItemRef?: RefCallback<HTMLElement>;
    onMenuItemMouseEnter?: () => void;
    onMenuItemClick?: (event: Event) => void;
    onMenuItemMouseMove?: () => void;
    onMenuItemMouseLeave?: () => void;
}

type WithMenuItemProps = MenuItemRequiredProps & MenuItemInjectedProps;

interface WithMenuItemOptions {
    highlightable?: boolean;
}

/**
 * P: type of own props of the wrapped component
 */
function withMenuItem<P extends MenuItemRequiredProps>(
    WrappedComponent: ComponentType<P>,
    options: WithMenuItemOptions = {}
): ComponentType<Omit<P, keyof MenuItemInjectedProps>> {
    return class extends Component<Omit<P, keyof MenuItemInjectedProps>> {
        static override contextType = MenuContext;

        baseRef = createRef<HTMLElement>();

        get #active(): boolean {
            const { disabled } = this.props;
            const { highlightable = true } = options;

            return !disabled && highlightable;
        }

        updateHighlightables = () => {
            const { indicesPath } = this.props;
            if (!indicesPath) {
                throw Error(
                    "withMenuItem HOCs require additional properties provided by toMenuItemChildren, " +
                    "use withMenuItem only in collobration with toMenuItemChildren"
                );
            }

            let currentHighlightables = (this.context as MenuContext).highlightables;

            for (let i = 0; i < indicesPath.length - 1; i++) {
                const index = indicesPath[i];
                currentHighlightables[index] ??= {} as HighlightableMenuItem;
                currentHighlightables[index].submenu ??= {};
                currentHighlightables = currentHighlightables[index].submenu!;
            }

            if (this.#active) {
                const currentIndex = indicesPath[indicesPath.length - 1];
                currentHighlightables[currentIndex] ??= {} as HighlightableMenuItem;
                currentHighlightables[currentIndex].ref = this.baseRef;
                currentHighlightables[currentIndex].submenu ??= null;
            }
        };

        handleMenuItemRef = (el: HTMLElement | null) => {
            this.baseRef.current = el;
        };

        handleMouseEnter = () => {
            if (!this.#active) {
                return;
            }

            const { indicesPath, handleMenuChildMouseEnter } = this.props;

            handleMenuChildMouseEnter?.(indicesPath!);
        };

        handleMouseMove = () => {
            if (!this.#active) {
                return;
            }

            const { indicesPath, handleMenuChildMouseMove } = this.props;

            handleMenuChildMouseMove?.(indicesPath!);
        };

        handleMouseLeave = () => {
            if (!this.#active) {
                return;
            }

            const { handleMenuChildMouseLeave } = this.props;

            handleMenuChildMouseLeave?.();
        };

        handleClick = (event: Event) => {
            const { value, handleMenuChildClick } = this.props;

            handleMenuChildClick?.(event, value);
        };

        override componentDidMount() {
            this.updateHighlightables();
        }

        override componentDidUpdate() {
            this.updateHighlightables();
        }

        render() {
            return (
                <WrappedComponent
                    {...(this.props as P)}
                    menuItemRef={this.baseRef}
                    onMenuItemMouseEnter={this.handleMouseEnter}
                    onMenuItemClick={this.handleClick}
                    onMenuItemMouseMove={this.handleMouseMove}
                    onMenuItemMouseLeave={this.handleMouseLeave}/>
            );
        }
    };
}

function toMenuItemChildren(
    children: VNode[],
    {
        parentIndicesPath = [], highlightedIndicesPath, lastHighlightableOpen,
        handleMenuChildClick, handleMenuChildMouseEnter, handleMenuChildMouseMove,
        handleMenuChildMouseLeave
    }: {
        parentIndicesPath?: string[];
        highlightedIndicesPath: string[];
        lastHighlightableOpen: boolean;
        handleMenuChildClick: (event: Event, value?: string) => void;
        handleMenuChildMouseEnter: (indicesPath: string[]) => void;
        handleMenuChildMouseMove?: (indicesPath: string[]) => void;
        handleMenuChildMouseLeave?: () => void;
    }
) {
    return children.map((child, index) => {
        const indicesPath = [...parentIndicesPath, index.toString()];
        const highlighted = arrayEqual(highlightedIndicesPath, indicesPath);
        const partialHighlightedIndicesPath = highlightedIndicesPath.slice(0, indicesPath.length);
        const open = arrayEqual(partialHighlightedIndicesPath, indicesPath) && (!highlighted || lastHighlightableOpen);
        return cloneElement(child, {
            indicesPath,
            highlighted,
            open,
            handleMenuChildClick,
            handleMenuChildMouseEnter,
            handleMenuChildMouseLeave,
            handleMenuChildMouseMove
        } as MenuChildrenInjectedProps);
    });
}

export default withMenuItem;
export {
    WithMenuItemProps,
    toMenuItemChildren
};
